import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Mail, X, Sparkles, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { CEF_LINKS } from "@/lib/cef-links";
import { CefLogoImg } from "@/components/site/BrandLogo";

const TikTokIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.27a8.16 8.16 0 0 0 4.77 1.52V7.4a4.85 4.85 0 0 1-1.84-.71z" /></svg>
);
const WhatsAppIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.84 9.84 0 0 0 12.04 2Zm5.83 14.04c-.25.7-1.43 1.33-2.01 1.42-.51.07-1.18.11-1.9-.12-.44-.14-1-.32-1.72-.63-3.03-1.31-5-4.36-5.16-4.56-.15-.2-1.22-1.62-1.22-3.09 0-1.47.77-2.19 1.04-2.49.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.07.92 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.18-.32.4-.45.54-.15.15-.31.31-.13.62.18.32.81 1.33 1.74 2.16 1.2 1.07 2.21 1.4 2.52 1.55.32.15.51.13.7-.08.2-.22.81-.94 1.03-1.27.22-.32.45-.27.74-.16.3.11 1.88.89 2.2 1.05.32.16.54.24.62.37.07.13.07.78-.18 1.48Z"/></svg>
);
const DiscordIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.211.375-.445.864-.609 1.249a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.073-.034 19.737 19.737 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.027c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.099.246.198.373.292a.077.077 0 0 1-.006.127c-.598.35-1.219.645-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.946 2.42-2.157 2.42Z"/></svg>
);

export default function FloatingHub() {
  const [open, setOpen] = React.useState(false);
  const [showContact, setShowContact] = React.useState(false);
  const [cfg, setCfg] = React.useState(null);
  const { user } = useAuth();

  React.useEffect(() => {
    api.get("/site-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const fab = cfg?.fab || {};

  return (
    <>
      {/* Standalone floating CEF logo — visible site-wide, opens lecef.org */}
      <a
        href={CEF_LINKS.website}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-4 left-4 z-[9998] w-12 h-12 inline-flex items-center justify-center bg-white border-2 border-black rounded-md shadow-brutalLg hover:scale-110 hover:-rotate-6 transition overflow-hidden"
        aria-label="Site officiel du CEF — lecef.org"
        data-testid="floating-cef-logo"
        title="Comité des Élèves Francophones"
      >
        <CefLogoImg className="w-10 h-10" />
      </a>

      <div className="fixed bottom-20 right-4 z-[9999]" data-testid="floating-hub">
      {open && (
        <div className="mb-3 neo-card bg-white p-4 w-72 max-w-[90vw]" data-testid="floating-hub-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-black text-sm uppercase tracking-tight flex items-center gap-1"><Sparkles className="w-4 h-4" /> Reste connecté·e</div>
            <button onClick={() => setOpen(false)} className="w-7 h-7 inline-flex items-center justify-center border-2 border-black rounded-md hover:bg-cef-yellow" data-testid="hub-close"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid gap-2">
            {fab.instagram_url && (
              <a href={fab.instagram_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-cef-pink text-white hover:translate-x-[1px]" data-testid="hub-instagram">
                <Instagram className="w-4 h-4" /> Instagram <ExternalLink className="w-3 h-3 ml-auto opacity-70" />
              </a>
            )}
            {fab.tiktok_url && (
              <a href={fab.tiktok_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-black text-white hover:translate-x-[1px]" data-testid="hub-tiktok">
                <TikTokIcon className="w-4 h-4" /> TikTok <ExternalLink className="w-3 h-3 ml-auto opacity-70" />
              </a>
            )}
            {fab.discord_url && (
              <a href={fab.discord_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-[#5865F2] text-white hover:translate-x-[1px]" data-testid="hub-discord">
                <DiscordIcon className="w-4 h-4" /> Discord <ExternalLink className="w-3 h-3 ml-auto opacity-70" />
              </a>
            )}
            {user ? (
              fab.whatsapp_url ? (
                <a href={fab.whatsapp_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-cef-green hover:translate-x-[1px]" data-testid="hub-whatsapp">
                  <WhatsAppIcon className="w-4 h-4" /> WhatsApp (membres) <ExternalLink className="w-3 h-3 ml-auto opacity-70" />
                </a>
              ) : null
            ) : (
              <Link to="/signup" className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-cef-yellow hover:translate-x-[1px]" data-testid="hub-whatsapp-locked">
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp (créer un compte)
              </Link>
            )}
            {fab.contact_form_url ? (
              <a href={fab.contact_form_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-white hover:translate-x-[1px]" data-testid="hub-contact">
                <Mail className="w-4 h-4" /> Formulaire contact <ExternalLink className="w-3 h-3 ml-auto opacity-70" />
              </a>
            ) : (
              <button onClick={() => setShowContact(true)} className="flex items-center gap-2 px-3 py-2 border-2 border-black rounded-md font-bold text-sm bg-white hover:translate-x-[1px] text-left" data-testid="hub-contact">
                <Mail className="w-4 h-4" /> Contact CEF
              </button>
            )}
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 inline-flex items-center justify-center bg-cef-pink text-white border-2 border-black rounded-full shadow-brutalLg hover:scale-105 transition"
        aria-label="Canaux de contact"
        data-testid="floating-hub-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {showContact && <ContactModal email={fab.contact_email || "info@lecef.org"} onClose={() => setShowContact(false)} />}
    </div>
    </>
  );
}

function ContactModal({ email, onClose }) {
  const [form, setForm] = React.useState({ name: "", email: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Contact site — ${form.name}`);
    const body = encodeURIComponent(`De: ${form.name} <${form.email}>\n\n${form.message}`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="contact-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="neo-card bg-white p-6 w-full max-w-md grid gap-3 my-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl tracking-tighter uppercase">Contacter le CEF</h3>
          <button type="button" onClick={onClose} className="w-7 h-7 inline-flex items-center justify-center border-2 border-black rounded-md hover:bg-cef-yellow" data-testid="contact-close"><X className="w-4 h-4" /></button>
        </div>
        <input className="neo-input" placeholder="Ton prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
        <input className="neo-input" type="email" placeholder="Ton email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
        <textarea className="neo-input min-h-[120px]" placeholder="Ton message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" />
        <button type="submit" className="neo-btn bg-cef-pink text-white" data-testid="contact-submit">Envoyer</button>
        <div className="text-xs text-neutral-500">Le message ouvrira ton client mail vers <strong>{email}</strong>.</div>
      </form>
    </div>
  );
}
